import type { RouteLocationRaw, RouteMeta, RouteParamsGeneric } from 'vue-router'
import { isApiError } from '@/api/client'
import { refusalCode } from '@/lib/intelligence-pages'
import type { WorkspaceAccess } from '@/types/api'

/**
 * A link to an Intelligence page the person cannot open sends them Home with
 * the reason (P1 §2.1; DECISIONS.md #17: this product's Home stands in for
 * ElfieCare's until the move). A convenience only: the API refuses the page's
 * data whatever this decides.
 *
 * `to.meta.capability` is what the page needs; the refusal code travels as
 * `?refused=`, the same code the API would send.
 */
export const guardIntelligencePage = async (
  to: { params: RouteParamsGeneric; meta: RouteMeta },
  fetchAccess: (workspaceId: string) => Promise<WorkspaceAccess>,
): Promise<true | RouteLocationRaw> => {
  const capability = to.meta.capability
  const workspaceId = to.params.workspaceId
  if (to.meta.layout !== 'intelligence' || !capability || typeof workspaceId !== 'string') return true

  try {
    const access = await fetchAccess(workspaceId)
    if (access.can[capability]) return true
    return { name: 'home', query: { refused: refusalCode(access, capability) } }
  } catch (error) {
    // A refusal is an answer; anything else is a failure the page shows, with its retry.
    if (isApiError(error) && [403, 404].includes(error.statusCode)) {
      return { name: 'home', query: { refused: error.code } }
    }
    return true
  }
}
